import { Link, NavLink, useLocation } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useQuery } from '@/api/query'
import { DEFAULT_HOME } from '@/auth/routes'
import { initials } from '@/lib/format'
import { may, maySee, useActiveTenant, useTenants } from '@/tenant/context'
import { BudgetMeter } from './BudgetMeter'
import { Lockup } from './Lockup'
import { countFor } from './counts'
import type { NavCounts } from './counts'
import { MODULES, isCurrent, moduleById } from './navigation'
import type { ModuleId, NavSection } from './navigation'

/**
 * The module switcher.
 *
 * Two buttons rather than a select, because it is a **mode** and both options
 * must be visible at a glance — S-D3's reasoning, and the reason it survives
 * the mobile collapse where ordinary navigation does not.
 *
 * **Two instances exist and only one is ever on screen** (SHELL-5): this one in
 * the sidebar, and `AppShell`'s inline copy for the width where the sidebar is
 * collapsed away. They carry different `label`s for the reason `MobileBar`
 * names itself *"(compact)"* — two groups with one name is a rail nobody can
 * tell apart, in a screen reader or in a test.
 */
export function ModuleSwitcher({
  active,
  label = 'Module',
  onChange,
}: {
  active: ModuleId
  /** What the group is called. See the note above on why there are two. */
  label?: string
  onChange: (id: ModuleId) => void
}): JSX.Element {
  return (
    <div className="app-switch" role="group" aria-label={label}>
      {MODULES.map((module) => (
        <button
          key={module.id}
          type="button"
          aria-pressed={module.id === active}
          onClick={() => {
            onChange(module.id)
          }}
        >
          {module.label}
        </button>
      ))}
    </div>
  )
}

/**
 * The number, or the badge, that follows a section's label — SHELL-1, SHELL-2.
 *
 * Two shapes, because `shell.html` draws two. *Sites 2* is a quiet figure in
 * the caption size at 70% opacity (`screens.css:180`); *Approvals 3* is an
 * `ar-badge--warning`, which is the design saying the two mean different
 * things — one is how much there is, the other is that somebody has to act.
 *
 * `aria-hidden` on neither. A count a screen reader skips is a count only
 * sighted users have, and *"Approvals, 3 waiting"* is exactly the thing the
 * design says a tenant opens the product for. The label the badge reads is
 * spelled out rather than left as a bare numeral, which on its own would be
 * announced as *"Approvals 3"* — indistinguishable from a section named that.
 */
function SectionCount({ section, count }: { section: NavSection; count: number }): JSX.Element {
  if (section.badge) {
    return (
      <span className="ar-badge ar-badge--warning app-nav__badge">
        {count}
        <span className="ar-visually-hidden"> waiting</span>
      </span>
    )
  }
  return <span className="app-nav__count">{count}</span>
}

/**
 * The section list for one module.
 *
 * **`Link` rather than `NavLink`, and `aria-current` decided here** (SHELL-3).
 * `NavLink` matches on its own `to`, which was right for seven entries and
 * wrong for *Settings*: it links to `/settings/organisation` because a settings
 * screen has to open on some panel, so the rail highlighted nothing at all on
 * the other eleven. `isCurrent` reads the section's `match` where it has one and
 * keeps the exact test for the module home, where `NavLink`'s `end` used to.
 *
 * *Settings* is the last entry of the list, where `shell.html:83` draws it
 * (parity P-SHELL-01, 2026-09-13). It sat in the rail's foot from 2026-09-10 to
 * 2026-09-13 at the owner's request; the parity fix put it back. Since
 * 2026-09-16 it is the way *out* of the shell rather than a section of it: the
 * settings screen is a page of its own (`SettingsShell`), so this entry leads
 * there and is never the current one.
 */
export function SectionNav({
  module,
  counts,
}: {
  module: ModuleId
  counts: NavCounts | null
}): JSX.Element {
  const definition = moduleById(module)
  const { pathname } = useLocation()
  const { membership } = useActiveTenant()
  return (
    <nav className="app-nav" aria-label={definition.label}>
      {/*
        SHELL-26. Only what this role can open. *"When the user is assigned to a
        particular role, the user should be able to access those pages only"* —
        the API refuses the rest regardless, and this is what stops the rail
        offering eleven links that all answer 403.

        An entry with no `page` is always listed; `NavSection.page` carries why.
      */}
      {definition.sections
        .filter((section) => section.page === undefined || maySee(membership, section.page))
        .map((section) => {
          const count = countFor(counts, section.count)
          return section.to ? (
            <Link
              key={section.label}
              to={section.to}
              aria-current={isCurrent(section, pathname, definition.home) ? 'page' : undefined}
            >
              {section.label}
              {count === null ? null : <SectionCount section={section} count={count} />}
            </Link>
          ) : (
            <span key={section.label} aria-disabled="true">
              {section.label}
              <span className="app-nav__soon">Soon</span>
            </span>
          )
        })}
    </nav>
  )
}

/**
 * The whole sidebar: lockup, switcher, sections, and the organisation card.
 *
 * The card is a button that goes to `/organisations` rather than an inline
 * dropdown. Switching organisation is a *session* write on the server, not a
 * client-side filter — it can fail, and it needs somewhere to say so.
 *
 * **The card is the whole foot row** — avatar, name, plan line — as
 * `shell.html:96-102` draws it (parity P-SHELL-02/07, 2026-09-13). The ⏻
 * sign-out button that sat beside it from 2026-09-10 is gone: it took the
 * width the plan line needed and the mockup draws nothing there. *Sign out*
 * lives in `TopBar`'s account menu above 1100px and in the drawer's account
 * block below it (`AppShell`), so the capability moved rather than went.
 */
export function Sidebar({
  module,
  counts,
  onModuleChange,
  onNavigate,
  variant = 'fixed',
}: {
  module: ModuleId
  /** SHELL-1. Read once by `AppShell` — see `useNavCounts` on why not here. */
  counts: NavCounts | null
  onModuleChange: (id: ModuleId) => void
  /** Called after any navigation — the mobile drawer closes itself with it. */
  onNavigate?: () => void
  variant?: 'fixed' | 'drawer'
}): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const { memberships } = useTenants()

  return (
    <aside
      className={variant === 'drawer' ? 'app-side app-side--drawer' : 'app-side'}
      onClick={(event) => {
        // One handler rather than a callback threaded through every link: the
        // drawer should close on any navigation inside it, including links a
        // later session adds without knowing this exists.
        if (event.target instanceof HTMLElement && event.target.closest('a')) onNavigate?.()
      }}
    >
      {/* Home, whichever module the rail is showing (spec: *Landing*). The
          module's own overview is the first entry of the section list below;
          the mark above it is the product's front door, and it went to the
          module home until 2026-09-13. */}
      <NavLink className="app-side__lockup" to={DEFAULT_HOME} end>
        <Lockup />
      </NavLink>

      <ModuleSwitcher active={module} onChange={onModuleChange} />
      <SectionNav module={module} counts={counts} />

      <div className="app-side__foot">
        {/* S-B7. Drawn above the organisation card, where `shell#settings` puts
            it, and absent entirely for a tenant with no cap — see BudgetMeter. */}
        <BudgetMeter />
        <NavLink className="app-tenant" to="/organisations">
          <span className="ar-avatar" aria-hidden="true">
            {initials(tenant.name)}
          </span>
          <span className="app-tenant__body">
            <strong>{tenant.name}</strong>
            {/* G22. `billing:read` decides which of the two is mounted — see
                  BillingSubline on why this is not a branch inside one. */}
            {may(membership, 'billing:read') ? (
              <BillingSubline organisations={memberships.length} />
            ) : (
              <TenantSubline organisations={memberships.length} />
            )}
          </span>
        </NavLink>
      </div>
    </aside>
  )
}

/**
 * The second line of the organisation card.
 *
 * The fallback, and what everyone sees until the subscription answers: the role
 * and — only for somebody who has more than one — the organisation count, which
 * is the affordance that says the card is a switcher.
 */
function TenantSubline({ organisations }: { organisations: number }): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  // SHELL-26. The role's own name, which is whatever this organisation called
  // it — `roleLabel(role)` would print *"Custom"*, which is a database value and
  // not a thing anybody has been told they are. The administrator has no role
  // row and therefore no name of its own, so its kind is the label.
  const label = membership.role_name ?? 'Administrator'
  // AUTH-19. The signup trial, counted down here rather than in
  // `BillingSubline`: a tenant inside it has no subscription at all, so the line
  // that reads one never renders and the countdown would never be seen. This is
  // also the line every *member* sees, and a trial ending is not owner-only news.
  //
  // Two segments at most, which is why the countdown replaces the organisation
  // count rather than joining it — the measurement in `BillingSubline`'s note
  // applies to this line at the same width.
  //
  // **A house account has no countdown** (2026-09-12). One of AugmenTrait's own
  // organisations never lapses, so the days its signup trial has left are not a
  // fact about anything; the line says what it is instead, because "5 days
  // left" on the company's own dashboard is exactly what sent somebody looking
  // for a billing page to pay ourselves through.
  const days = tenant.access === 'house' ? null : trialDaysLeft(tenant.trial_ends_at)
  if (days !== null) {
    return (
      <span>
        {label} · {days === 1 ? '1 day' : `${String(days)} days`} left
      </span>
    )
  }
  if (tenant.access === 'house') {
    return <span>{label} · House account</span>
  }
  return (
    <span>
      {label}
      {organisations > 1 ? ` · ${String(organisations)} organisations` : ''}
    </span>
  )
}

/**
 * G22 — `shell#settings` draws this line as *"Growth · 9 days left"*.
 *
 * **Mounted only for a role that holds `billing:read`.** The subscription is an
 * owner-and-admin read (`app/v1/billing.py`), so for a member this component
 * does not exist and no request is made — rather than one fired on every
 * navigation for the pleasure of a 403. That is why this is a component and not
 * a branch inside `Sidebar`: the query hook has to be unconditional wherever it
 * lives, so the condition is which component gets mounted.
 *
 * **Every unusable answer falls back rather than blanking**, following
 * `BudgetMeter`: loading, a failed request, no subscription at all. A sidebar is
 * not where a failed request should be reported, and a skeleton in the foot of
 * every page is a flicker on every navigation.
 *
 * *"9 days left"* is shown only while the status is actually `trialing` — the
 * same condition `BillingPage` gates its *Trial ends* row on. `trial_ends_at`
 * outlives the trial on a converted subscription, so reading the date alone
 * would go on counting down days for a tenant who is paying.
 *
 * **Two segments, never three**, which is why the organisation count is the
 * fallback's and not this line's. Measured in a browser at the real 17rem rail:
 * *"Growth · 9 days left"* is 113px and every plan name fits on one line, while
 * appending *"· 2 organisations"* wraps to two and grows the card from 73px to
 * 100px. `.app-tenant__body span` has no `text-overflow`, unlike the `strong`
 * above it, so a long line wraps rather than clipping — and the card is a link
 * to the switcher whether or not it prints a count.
 */
function BillingSubline({ organisations }: { organisations: number }): JSX.Element {
  const { tenant } = useActiveTenant()
  const subscription = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/subscription', { signal })),
    [],
  )

  // A house account before the subscription is even read: it is never billed,
  // so a plan it chose before staff flagged it — the live tenant's `Starter ·
  // 1 day left` on 2026-09-19, hours after being flagged — is not a countdown
  // to anything. `TenantSubline` prints what it is.
  if (tenant.access === 'house' || subscription.state.status !== 'ready') {
    return <TenantSubline organisations={organisations} />
  }
  const current = subscription.state.data.subscription
  if (!current) return <TenantSubline organisations={organisations} />

  const days = current.status === 'trialing' ? trialDaysLeft(current.trial_ends_at) : null
  // AUTH-19. The subscription's own trial, still — this branch is only reached
  // when there *is* a subscription. The signup trial, which has none, is counted
  // down by `TenantSubline` from the organisation's own `trial_ends_at`.
  return (
    <span>
      {current.plan_name}
      {days === null ? '' : ` · ${days === 1 ? '1 day' : `${String(days)} days`} left`}
    </span>
  )
}

/**
 * Whole days from now until a trial ends, or `null` when there is no useful
 * number to print.
 *
 * Rounded **up**, because a trial with eleven hours to run is a trial with a day
 * left rather than none — and rounding down would print *"0 days left"* to a
 * tenant who still has the evening. An expired or missing date returns `null`
 * rather than a negative: past the end there is no countdown to state, and the
 * status will have moved off `trialing` anyway.
 */
function trialDaysLeft(endsAt: string | null | undefined): number | null {
  if (!endsAt) return null
  const remaining = Date.parse(endsAt) - Date.now()
  if (Number.isNaN(remaining) || remaining <= 0) return null
  return Math.ceil(remaining / 86_400_000)
}
